import {
  type FocusEvent,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { AppIcon, type StickyNote, type StickyNoteColor } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  type AutosaveStatus,
  useDebouncedAutosave,
} from '@backrs_ui/grow-ui/src/hooks/useDebouncedAutosave';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useDeleteStickyNoteMutation,
  useUpdateStickyNoteMutation,
} from '@backrs_ui/grow-ui/src/redux/stickyNotes/stickyNotes.api';
import {
  removePristineId,
  selectIsStickyNotePristine,
} from '@backrs_ui/grow-ui/src/redux/stickyNotes/stickyNotes.slice';
import type { StickyNoteTarget } from '@backrs_ui/grow-ui/src/redux/stickyNotes/stickyNotes.types';

import {
  Box,
  ButtonBase,
  IconButton,
  InputBase,
  keyframes,
  Popover,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { type Position, useDraggable } from './hooks/useDraggable';
import {
  getStickyNoteColor,
  getStickyNoteTextColor,
  STICKY_NOTE_HEIGHT,
  STICKY_NOTE_MAX_LENGTH,
  STICKY_NOTE_STRIP_HEIGHT,
  STICKY_NOTE_SWATCH_ORDER,
  STICKY_NOTE_WIDTH,
} from './stickyNotes.constants';

const STRINGS = appStrings.modules.priorities.stickyNotes;

const ORIGIN: Position = { x: 0, y: 0 };

const SAVE_STATUS_TEXT: Record<AutosaveStatus, string | null> = {
  idle: null,
  saving: STRINGS.saving,
  saved: STRINGS.saved,
  error: STRINGS.saveError,
};

/** Absolute placement on the board; without it the card sits in normal flow, full width. */
export type StickyNotePlacement = Readonly<{
  /** Saved position, clamped to the board for display. */
  position: Position;
  boundsRef: RefObject<HTMLElement | null>;
  isDragging: boolean;
  /** Stacks above the other notes. */
  isRaised: boolean;
  onDragStart: () => void;
  onDragEnd: (position: Position) => void;
}>;

type StickyNoteCardProps = Readonly<{
  note: StickyNote;
  target: StickyNoteTarget;
  readOnly: boolean;
  placement?: StickyNotePlacement;
  /** Adds a new note; shown as a control on the card when provided. Rejects on failure. */
  onCreate?: () => Promise<void>;
  /** Text typed before mount; replaces the saved text, saves once, and opens the editor. */
  seedText?: string;
}>;

// Keeps a press on a control from starting a drag on the strip beneath it.
const stopPointerDown = (e: PointerEvent) => e.stopPropagation();

export const StickyNoteCard = ({
  note,
  target,
  readOnly,
  placement,
  onCreate,
  seedText,
}: StickyNoteCardProps) => {
  const dispatch = useAppDispatch();
  const isPristine = useAppSelector(selectIsStickyNotePristine(note.id));
  const [updateStickyNote] = useUpdateStickyNoteMutation();
  const [deleteStickyNote] = useDeleteStickyNoteMutation();

  const cardRef = useRef<HTMLDivElement | null>(null);
  const fallbackBoundsRef = useRef<HTMLElement | null>(null);
  const [text, setText] = useState(note.text);
  // A new blank note opens in edit mode when its card mounts.
  const [isEditing, setIsEditing] = useState(
    () => (isPristine || seedText !== undefined) && !readOnly,
  );

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Read during blur, which can fire before the popover's open state has rendered.
  const isMenuOpenRef = useRef(false);

  const isDraggable = Boolean(placement) && !readOnly;
  // Controls stay shown without hover in the list layout and while the popover is open.
  const controlsPinned = !placement || Boolean(menuAnchor);

  const { position, dragHandleProps } = useDraggable({
    initial: placement?.position ?? ORIGIN,
    boundsRef: placement?.boundsRef ?? fallbackBoundsRef,
    nodeRef: cardRef,
    onDragStart: placement?.onDragStart,
    onDragEnd: placement?.onDragEnd,
  });

  const save = useCallback(
    async (value: string) => {
      await updateStickyNote({ ...target, id: note.id, text: value }).unwrap();

      return undefined;
    },
    [updateStickyNote, target, note.id],
  );

  const { status, schedule, cancel } = useDebouncedAutosave({ save });

  useEffect(() => {
    if (seedText === undefined || readOnly) return;

    setIsEditing(true);
    if (seedText !== note.text) {
      setText(seedText);
      schedule(seedText);
    }
  }, [seedText]); // eslint-disable-line react-hooks/exhaustive-deps

  const markTouched = () => {
    if (isPristine) dispatch(removePristineId(note.id));
  };

  const handleTextChange = (value: string) => {
    setText(value);
    setActionError(null);
    markTouched();
    schedule(value);
  };

  const handleColorChange = (color: StickyNoteColor) => {
    setActionError(null);
    markTouched();
    if (color !== note.color) updateStickyNote({ ...target, id: note.id, color });
  };

  const handleCreate = () => {
    setActionError(null);
    onCreate?.().catch(() => setActionError(STRINGS.createError));
  };

  // Text is read-only mid-delete, so a failed delete can reschedule the edit `cancel` dropped.
  const handleDelete = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    setActionError(null);
    cancel();

    try {
      await deleteStickyNote({ ...target, id: note.id }).unwrap();
    } catch {
      setIsDeleting(false);
      setActionError(STRINGS.deleteError);
      if (text !== note.text) schedule(text);
    }
  };

  const footerMessage =
    actionError && status !== 'error'
      ? { key: 'action-error', text: actionError, sx: SAVE_STATUS_SX.error }
      : { key: status, text: SAVE_STATUS_TEXT[status], sx: SAVE_STATUS_SX[status] };

  const openMenu = (e: MouseEvent<HTMLButtonElement>) => {
    isMenuOpenRef.current = true;
    setMenuAnchor(e.currentTarget);
  };

  const closeMenu = () => {
    isMenuOpenRef.current = false;
    setMenuAnchor(null);
  };

  // The color popover renders outside the card, so focus moving into it also stays put.
  const handleCardBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (
      isDeleting ||
      cardRef.current?.contains(e.relatedTarget as Node | null) ||
      isMenuOpenRef.current
    ) {
      return;
    }

    if (isPristine && !text) {
      dispatch(removePristineId(note.id));
      deleteStickyNote({ ...target, id: note.id });
    }
  };

  return (
    <Box
      ref={cardRef}
      id={`sticky-note-card-${note.id}`}
      tabIndex={readOnly ? undefined : 0}
      onBlur={handleCardBlur}
      sx={cardSx(
        placement ? { ...position, isRaised: placement.isRaised } : null,
        placement?.isDragging ?? false,
        note.color,
      )}
    >
      <Box
        id={`sticky-note-strip-${note.id}`}
        {...(isDraggable ? dragHandleProps : {})}
        sx={stripSx(isDraggable, placement?.isDragging ?? false)}
      >
        {!readOnly && onCreate && (
          <Box className="sticky-note-controls" sx={controlsSx(controlsPinned)}>
            <IconButton
              id={`sticky-note-create-button-${note.id}`}
              aria-label={STRINGS.newNoteAria}
              size="small"
              onPointerDown={stopPointerDown}
              onClick={handleCreate}
              sx={iconButtonSx}
            >
              <Box sx={addCircleSx}>
                <AppIcon iconName="Plus" diameter="12px" color="Colors/Foreground/fg-tertiary" />
              </Box>
            </IconButton>
          </Box>
        )}

        {!readOnly && (
          <Box className="sticky-note-controls" sx={controlsSx(controlsPinned, true)}>
            <IconButton
              id={`sticky-note-color-button-${note.id}`}
              aria-label={STRINGS.colorPickerAria}
              aria-haspopup="true"
              aria-expanded={Boolean(menuAnchor)}
              size="small"
              onPointerDown={stopPointerDown}
              onClick={openMenu}
              sx={iconButtonSx}
            >
              <Box sx={targetRingSx(16)}>
                <Box sx={targetRingSx(10)} />
              </Box>
            </IconButton>
          </Box>
        )}

        {!readOnly && (
          <IconButton
            id={`sticky-note-delete-button-${note.id}`}
            aria-label={STRINGS.deleteNote}
            size="small"
            onPointerDown={stopPointerDown}
            onClick={handleDelete}
            sx={iconButtonSx}
          >
            <AppIcon iconName="Trash01" diameter="16px" color="Colors/Foreground/fg-tertiary" />
          </IconButton>
        )}
      </Box>

      <Box sx={bodySx}>
        {isEditing ? (
          <InputBase
            id={`sticky-note-input-${note.id}`}
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            readOnly={isDeleting}
            onFocus={(e) =>
              e.target.setSelectionRange(e.target.value.length, e.target.value.length)
            }
            onBlur={() => setIsEditing(false)}
            inputProps={{ maxLength: STICKY_NOTE_MAX_LENGTH, 'aria-label': STRINGS.editAria }}
            placeholder={STRINGS.starterPlaceholder}
            autoFocus
            multiline
            fullWidth
            sx={inputSx(note.color)}
          />
        ) : (
          <Box
            id={`sticky-note-text-${note.id}`}
            onClick={readOnly ? undefined : () => setIsEditing(true)}
            sx={readTextSx(readOnly)}
          >
            {text ? (
              <Typography variant="text-md-regular" sx={textSx(note.color)}>
                {text}
              </Typography>
            ) : (
              <Typography variant="text-md-regular" sx={placeholderSx}>
                {STRINGS.starterPlaceholder}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {(isDraggable || footerMessage.text) && (
        <Box sx={footerSx}>
          {footerMessage.text && (
            <Typography
              // Remounts per message so the saved fade restarts each time.
              key={footerMessage.key}
              id={`sticky-note-save-status-${note.id}`}
              variant="text-xs-regular"
              aria-live="polite"
              sx={footerMessage.sx}
            >
              {footerMessage.text}
            </Typography>
          )}
        </Box>
      )}

      {!readOnly && (
        <Popover
          id={`sticky-note-options-${note.id}`}
          open={Boolean(menuAnchor)}
          anchorEl={menuAnchor}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: menuPaperSx } }}
        >
          <Box sx={swatchRowSx}>
            {STICKY_NOTE_SWATCH_ORDER.map((color) => (
              <ButtonBase
                key={color}
                id={`sticky-note-swatch-${note.id}-${color}`}
                aria-label={STRINGS.colorAria[color]}
                aria-pressed={color === note.color}
                onClick={() => handleColorChange(color)}
                sx={swatchSx(color, color === note.color)}
              />
            ))}
          </Box>
        </Popover>
      )}
    </Box>
  );
};

/** The flat, bordered note surface without placement or controls. */
export const stickyNoteSurfaceSx = (theme: Theme, color: StickyNoteColor) => {
  const black = theme.backrs.colors['Colors/Base/Black'];

  return {
    height: STICKY_NOTE_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: getStickyNoteColor(theme, color),
    border: `1px solid ${black}`,
    borderRadius: 0,
    boxShadow: theme.backrs.shadows['shadow-sm'],
  } as const;
};

// Shifts up-left as the shadow grows, so the shadow's far edge stays put while dragged.
const LIFT_OFFSET = 'translate(-2px, -2px)';
const LIFT_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

const cardSx =
  (
    position: (Position & { isRaised: boolean }) | null,
    isDragging: boolean,
    color: StickyNoteColor,
  ): SxProps<Theme> =>
  (theme) => ({
    ...stickyNoteSurfaceSx(theme, color),
    ...(position
      ? {
          position: 'absolute',
          top: position.y,
          left: position.x,
          zIndex: isDragging ? 3 : position.isRaised ? 2 : 1,
          width: STICKY_NOTE_WIDTH,
          pointerEvents: 'auto',
        }
      : { position: 'relative', width: STICKY_NOTE_WIDTH, maxWidth: '100%' }),
    outline: 'none',
    transform: isDragging ? LIFT_OFFSET : 'none',
    ...(isDragging && { boxShadow: theme.backrs.shadows['shadow-lg'] }),
    transition: `transform 200ms ${LIFT_EASING}, box-shadow 200ms ${LIFT_EASING}`,
    '&:hover .sticky-note-controls, &:focus-within .sticky-note-controls': {
      opacity: 1,
      visibility: 'visible',
      transitionDelay: '0s',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  });

const stripSx =
  (isDraggable: boolean, isDragging: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    height: STICKY_NOTE_STRIP_HEIGHT,
    px: backrs.spacing['spacing-md'],
    ...(isDraggable && {
      touchAction: 'none',
      userSelect: 'none',
      cursor: isDragging ? 'grabbing' : 'grab',
    }),
  });

const controlsSx =
  (alwaysVisible: boolean, alignEnd = false): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    ...(alignEnd && { ml: 'auto' }),
    alignItems: 'center',
    gap: backrs.spacing['spacing-xs'],
    opacity: alwaysVisible ? 1 : 0,
    visibility: alwaysVisible ? 'visible' : 'hidden',
    // Visibility flips after the fade-out so the controls stay clickable until hidden.
    transition: 'opacity 150ms ease, visibility 0s linear 150ms',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  });

const swatchSx =
  (color: StickyNoteColor, isCurrent: boolean): SxProps<Theme> =>
  (theme) => ({
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: getStickyNoteColor(theme, color),
    border: `1px solid ${theme.backrs.colors['Colors/Base/Black']}`,
    outline: isCurrent ? `2px solid ${theme.backrs.colors['Colors/Base/Black']}` : 'none',
    outlineOffset: 1,
    cursor: 'pointer',
  });

const iconButtonSx: SxProps<Theme> = ({ backrs }) => ({
  p: backrs.spacing['spacing-xs'],
  borderRadius: backrs.radius['radius-xs'],
});

const addCircleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  width: 16,
  height: 16,
  border: `1px dashed ${backrs.colors['Colors/Foreground/fg-tertiary']}`,
  borderRadius: backrs.radius['radius-full'],
});

const targetRingSx =
  (size: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: size,
    height: size,
    border: `1.5px solid ${backrs.colors['Colors/Foreground/fg-tertiary']}`,
    borderRadius: backrs.radius['radius-full'],
  });

const menuPaperSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-xs'],
  p: backrs.spacing['spacing-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-md'],
  boxShadow: backrs.shadows['shadow-sm'],
});

const swatchRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-md'],
  px: backrs.spacing['spacing-xs'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  px: backrs.spacing['spacing-xl'],
  pb: backrs.spacing['spacing-lg'],
});

const inputSx =
  (color: StickyNoteColor): SxProps<Theme> =>
  (theme) => ({
    alignItems: 'flex-start',
    p: 0,
    ...theme.typography['text-md-regular'],
    color: getStickyNoteTextColor(theme, color),
  });

const readTextSx =
  (readOnly: boolean): SxProps<Theme> =>
  () => ({
    minHeight: '100%',
    cursor: readOnly ? 'default' : 'text',
  });

const textSx =
  (color: StickyNoteColor): SxProps<Theme> =>
  (theme) => ({
    color: getStickyNoteTextColor(theme, color),
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  });

const placeholderSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const statusSx: SxProps<Theme> = ({ backrs }) => ({
  ml: 'auto',
  color: backrs.colors['Colors/Text/text-tertiary'],
});

// Holds briefly, then fades; the label stays in layout so the body doesn't shift.
const savedFadeKeyframes = keyframes`
  0%, 75% { opacity: 1; }
  100% { opacity: 0; }
`;

const savedStatusSx: SxProps<Theme> = ({ backrs }) => ({
  ml: 'auto',
  color: backrs.colors['Colors/Text/text-tertiary'],
  animation: `${savedFadeKeyframes} 2s ease forwards`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 0,
  },
});

const footerSx: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  minHeight: STICKY_NOTE_STRIP_HEIGHT,
  px: backrs.spacing['spacing-md'],
});

const errorStatusSx: SxProps<Theme> = ({ backrs }) => ({
  ml: 'auto',
  color: backrs.colors['Colors/Text/text-error-primary'],
});

const SAVE_STATUS_SX: Record<AutosaveStatus, SxProps<Theme>> = {
  idle: statusSx,
  saving: statusSx,
  saved: savedStatusSx,
  error: errorStatusSx,
};
